import * as React from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/styles";
import { Card, Divider, Typography } from "@material-ui/core";
import { Alert } from "@material-ui/lab";

import DeleteIcon from "mdi-material-ui/DeleteCircleOutline";

// perfect scrollbar
import "react-perfect-scrollbar/dist/css/styles.css";
import PerfectScrollbar from "react-perfect-scrollbar";

import { Button, translate } from "jazasoft";
import Table from "jazasoft/lib/mui/components/Table";

import MuiTable from "../../../components/MuiTable";
import Tabs from "../../../components/tab/Tabs";
import SubTab from "../../../components/tab/SubTab";
import CardHeader from "../../../components/CardHeader";

import { isEmpty } from "../../../utils/helpers";

import useCutOrder from "./useCutOrder";
import { ShadeRowRenderer } from "./ShadeRowRenderer";
import { OState } from "../../../utils/types";
import hasPrivilege from "../../../utils/hasPrivilege";
import { ResourceId, Scope } from "../../../utils/privilege";
import { colorTomatoRed } from "../../../utils/colors";

const useStyles = makeStyles({
  content: {
    margin: "1.5em",
    marginTop: 0
  },
  mrTop: {
    marginTop: "1.5em"
  },
  buttons: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    padding: "1.5em"
  },
  table: {
    // "& th, & td": {
    //   border: "1px solid rgba(224, 224, 224, 1)"
    // },
    whiteSpace: "pre"
  },
  footer: {
    padding: "1.5em",
    display: "flex",
    justifyContent: "center"
  }
});

const CutOrder = props => {
  const classes = useStyles();
  const { translate, roles = [], hasAccess } = props;
  const id = props.match?.params?.id;
  const {
    busy,
    version,
    errorMessage,
    selectedKeys,
    order,
    flowInfoColumns,
    flowInfoRows,
    fabricInfoColumns,
    fabricInfoRows,
    shadeColumns,
    shadeRows,
    tabIdx,
    setTabIdx,
    setSelectedKeys,
    onInlineActionClick,
    createCutOrder
  } = useCutOrder(props);

  const history = useHistory();

  const onSelect = selectedKeys => {
    setSelectedKeys(selectedKeys);
  };

  const onRowClick = row => {
    if (tabIdx === 1) return;
    history.push(`/cutPlan/bulk/${id}/${row.cutOrder?.id}`);
  };

  const defaultExpanded = flowInfoRows.filter(fi => isEmpty(fi.parentKey)).length === 1;

  const inlineActions = row => {
    if (!isEmpty(row.parentKey)) return [];
    let actions = [
      { name: "delete", tooltip: "Delete Cut Order", icon: <DeleteIcon fontSize="large" />, options: { style: { color: colorTomatoRed.dark } } }
    ];
    if (
      row.cutOrder?.stateValue > OState.FINAL_GROUPING_COMPLETED.stateValue ||
      !hasPrivilege(roles, hasAccess, ResourceId.CUT_ORDER, Scope.DELETE)
    ) {
      actions = actions.filter(a => a.name !== "delete");
    }
    return actions;
  };

  if (isEmpty(order)) {
    return <div />;
  }

  return (
    <div className={classes.content}>
      <Tabs style={{ marginTop: "1.5em" }} value={tabIdx} onChange={(e, idx) => setTabIdx(idx)}>
        <SubTab label={translate("resources.cutPlan.title.cut_order", { _: "Cut Order" })} />
        <SubTab label={translate("resources.cutPlan.title.readyToPlan", { _: "Ready to Plan" })} />
      </Tabs>

      {/** Error Message */}
      {!isEmpty(errorMessage) && (
        <Alert variant="filled" severity="error" style={{ marginBottom: "1.5em", marginTop: "1.5em" }}>
          <Typography variant="h6">{errorMessage}</Typography>
        </Alert>
      )}

      {/* Flow Details */}
      <div style={{ marginTop: "1.5em" }}>
        <MuiTable
          key={version}
          title={<CardHeader style={{ padding: 0 }} title={"Order Details"} />}
          toolbar={true}
          searchable={true}
          pageable={true}
          searchKeys={["style", "color", "flowRef", "customerFlowRef", "fpo"]}
          idKey="key"
          parentIdKey="parentKey"
          columns={flowInfoColumns}
          rows={flowInfoRows}
          defaultExpanded={defaultExpanded}
          expandedColor={["#BFDDF8", "#7AB9F0"]}
          selectAll={false}
          selectable={tabIdx === 1 && hasPrivilege(roles, hasAccess, ResourceId.CUT_ORDER, Scope.WRITE)}
          selectActions={[]}
          inlineActions={tabIdx === 0 ? inlineActions : []}
          onSelect={onSelect}
          onRowClick={onRowClick}
          onInlineActionClick={onInlineActionClick}
          i18nKey="resources.bulk.fields.flowInfo"
        />
      </div>

      {/* Fabric Details */}
      {tabIdx === 1 && !isEmpty(fabricInfoRows) && isEmpty(errorMessage) && (
        <div className={classes.mrTop}>
          <MuiTable
            component="div"
            title="Fabric Details"
            toolbar
            columns={fabricInfoColumns}
            rows={fabricInfoRows}
            i18nKey="resources.grouping.fields.flowInfo"
          />
        </div>
      )}

      {/* Shade Allocation */}
      {!isEmpty(selectedKeys) && !isEmpty(shadeColumns) && (
        <Card className={classes.mrTop}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <CardHeader title="Fabric & Shade Allocation" titleTypographyProps={{ variant: "h6" }} />
          </div>
          <Divider />
          <PerfectScrollbar>
            <Table
              classes={{ table: classes.table }}
              columns={shadeColumns}
              rows={shadeRows}
              rowRenderer={ShadeRowRenderer}
              i18nKey="resources.grouping.fields.shadeAllocation"
            />
          </PerfectScrollbar>
        </Card>
      )}

      {tabIdx === 1 && hasPrivilege(roles, hasAccess, ResourceId.CUT_ORDER, Scope.WRITE) && (
        <div className={classes.footer}>
          <Button label="Create Cut Order" variant="contained" color="primary" disabled={busy || isEmpty(selectedKeys)} onClick={createCutOrder} />
        </div>
      )}
    </div>
  );
};

export default translate(CutOrder);
